// Authored.by.KWShin.2025.11.25.
import { merge, isFunction } from 'lodash';
import { api } from '@/api/common/instances';
import { comma, kilobytes, bytes } from '@/utils/formatter';
import chartable from '@/mixins/chartable';
import motletable from '@/mixins/motletable';
import BarChart from '../../../components/BarChart';

export default {
  components: {
    BarChart,
  },
  mixins: [motletable, chartable],
  props: {
    sstmId: {
      type: String,
      required: true,
    },
    storeName: {
      type: String,
      required: true,
    },
    timestamp: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      data: [],
      motletOptions: {
        monitor: {
          systemId: this.sstmId,
        },
        descendant: {
          systemId: this.sstmId,
        },
      },
    };
  },
  computed: {
    descendants() {
      return this.$store.getters[`${this.storeName}/get`];
    },
    categoryField() {
      return this.motletState.monitor.categoryField;
    },
    settings() {
      if (this.data.length === 0) {
        return {};
      }
      const percent = this.chartField.type == 'PERCENT';
      const xAxis = {};
      const yAxis = {
        name: `${this.chartField.text} ${percent ? '(%)' : ''}`,
        max: percent ? 100 : undefined,
        maxInterval: percent ? 25 : undefined,
        splitNumber: percent ? 4 : undefined,
      };
      const dataset = (() => {
        // data 내의 모든 categoryField 를 취합하여 중복제거 & 정렬.
        const categories = [...new Set(this.data.reduce((acc, item) => acc.concat(item.data.map(s => s[this.categoryField])), []))].sort();
        const header = ['name'].concat(this.descendants.map(d => d.instanceId + d.id));
        const dataset = categories.map(category => {
          return [category].concat(
            this.descendants
              .filter(dsdnt => dsdnt.active)
              .map(dsdnt => {
                const index = this.descendants.findIndex(item => item.instanceId + item.id == dsdnt.instanceId + dsdnt.id);
                const target = this.data[index] ? this.data[index].data.find(item => item[this.categoryField] == category) : null;
                return (target || {})[this.chartField.code];
              }),
          );
        });
        return { source: [header].concat(dataset) };
      })();
      const series = this.descendants
        .filter(dsdnt => dsdnt.active)
        .map(dsdnt => {
          return {
            id: `${dsdnt.instanceId}_${dsdnt.id}`,
            type: 'bar',
            itemStyle: {
              color: dsdnt.color,
            },
          };
        });
      const valueFormatter = (data, type = this.chartField.type) => {
        const formatted = {
          unit: '',
          data: '',
        };
        const isEmpty = data === undefined || data === null || data === '';
        data = isEmpty ? '' : data;
        if (type == 'PERCENT') {
          formatted.unit = isEmpty ? '' : ' %';
          formatted.data = data;
        } else if (type == 'DIGIT') {
          formatted.data = comma(data);
        } else if (type == 'KILO_BYTE') {
          formatted.data = kilobytes(data);
        } else if (type == 'BYTE') {
          formatted.data = bytes(data);
        } else {
          formatted.data = data;
        }
        return `${formatted.data}${formatted.unit}`;
      };
      const formatter = {
        tooltip: params => {
          const param = params[0];
          const contents = [];
          contents.push('<i class="v-icon notranslate mdi mdi-code-greater-than mr-1" style="font-size: 12px; line-height: 10px"></i>');
          contents.push(param.name);
          contents.push('<br />');

          params.forEach((p, index) => {
            if (p.data[index + 1] === undefined) return;
            const ids = p.seriesId.split('_');
            const instId = ids[0];
            const id = ids[1];
            const descendant = this.descendants.find(d => d.instanceId == instId && d.id == id);
            const color = descendant.color;
            contents.push(this.circle(color));
            contents.push(`<span style="color:${color};font-weight:900;">`);
            contents.push(id);
            contents.push('</span> : ');
            contents.push(valueFormatter(p.data[index + 1]));
            contents.push('<br />');
          });

          return contents.join('');
        },
      };
      const defaults = {
        chart: {
          xAxis,
          yAxis,
          dataset,
          series,
        },
        formatter,
      };
      return merge({}, defaults, isFunction(this.getSettings) ? this.getSettings() : undefined);
    },
  },
  methods: {
    async fetchData(ymdhms) {
      const sstmId = this.sstmId;
      const motletCode = this.monitor.monitorCode;
      const params = {
        beginYmdhms: ymdhms,
        endYmdhms: ymdhms,
        sstmId,
        motletCode,
        memKind: this.motletOptions.dataParam.memKind,
      };

      const { data: results } = await api.get('/motlet-data/chart', { params });

      this.data = results.map(result => {
        const id = result.key.instId + result.key.nodeId;
        const data = result.data;
        return { id, data };
      });
    },
  },
};
